import React from 'react'
import { Path, PathValue, UnpackNestedValue } from 'react-hook-form'
import Error from '~components/dumbs/Error'
import { Props } from './interfaces'
import * as Styled from './styled'

function CodeField<TFieldValues>({
  name,
  setFocus,
  register,
  error,
  onChange,
  getValues,
  hasSomethingTyped,
  setValue,
}: Props<TFieldValues>) {
  const firstInputName = `${name}.0` as Path<TFieldValues>
  const secondInputName = `${name}.1` as Path<TFieldValues>
  const thirdInputName = `${name}.2` as Path<TFieldValues>
  const fourthInputName = `${name}.3` as Path<TFieldValues>
  const firstReactHookFormProps = register(firstInputName)
  const secondReactHookFormProps = register(secondInputName)
  const thirdReactHookFormProps = register(thirdInputName)
  const fourthReactHookFormProps = register(fourthInputName)

  const showValidation = !!getValues(fourthInputName)
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const hasInputChangedToEmpty =
      !getValues(event.target.name as Path<TFieldValues>) && !event.target.value

    onChange()

    const options = {
      [firstInputName]: firstReactHookFormProps.onChange,
      [secondInputName]: secondReactHookFormProps.onChange,
      [thirdInputName]: thirdReactHookFormProps.onChange,
      [fourthInputName]: fourthReactHookFormProps.onChange,
    }

    void options[event.target.name](event)

    const isKeyANumber = /[0-9]+/i.test(event.target.value)

    if (isKeyANumber) {
      const options = {
        [firstInputName]: () => setFocus(secondInputName),
        [secondInputName]: () => setFocus(thirdInputName),
        [thirdInputName]: () => setFocus(fourthInputName),
      }
      options[event.target.name]?.()
    }

    if (hasInputChangedToEmpty) {
      const options = {
        [secondInputName]: () => setFocus(firstInputName),
        [thirdInputName]: () => setFocus(secondInputName),
        [fourthInputName]: () => setFocus(thirdInputName),
      }
      options[event.target.name]?.()
    }
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const target = event.nativeEvent.target as HTMLInputElement
    const hasAlreadyANumber = target.value.length === 1
    const hasPressedBackspace = event.keyCode === 8

    if (event.key === 'Enter') {
      return
    }

    if (hasPressedBackspace && !target.value) {
      const options = {
        [secondInputName]: () => setFocus(firstInputName),
        [thirdInputName]: () => setFocus(secondInputName),
        [fourthInputName]: () => setFocus(thirdInputName),
      }
      options[target.name]?.()
    }

    if (hasAlreadyANumber && !hasPressedBackspace) {
      const options = {
        [firstInputName]: () => setFocus(secondInputName),
        [secondInputName]: () => setFocus(thirdInputName),
        [thirdInputName]: () => setFocus(fourthInputName),
        [fourthInputName]: () => event.preventDefault(),
      }
      options[target.name]?.()
    }
  }

  const handleInput = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const target = event.nativeEvent.target as HTMLInputElement
    target.value = target.value.replace(/[^0-9]/g, '')
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const target = event.nativeEvent.target as HTMLInputElement

    const verifyCode = event.clipboardData
      .getData('Text')
      .replace(/[^0-9]/g, '')
      .split('')

    setValue(
      firstInputName,
      verifyCode?.[0] as UnpackNestedValue<PathValue<TFieldValues, Path<TFieldValues>>>,
    )
    setValue(
      secondInputName,
      verifyCode?.[1] as UnpackNestedValue<PathValue<TFieldValues, Path<TFieldValues>>>,
    )
    setValue(
      thirdInputName,
      verifyCode?.[2] as UnpackNestedValue<PathValue<TFieldValues, Path<TFieldValues>>>,
    )
    setValue(
      fourthInputName,
      verifyCode?.[3] as UnpackNestedValue<PathValue<TFieldValues, Path<TFieldValues>>>,
    )

    const options = {
      [firstInputName]: firstReactHookFormProps.onChange,
      [secondInputName]: secondReactHookFormProps.onChange,
      [thirdInputName]: thirdReactHookFormProps.onChange,
      [fourthInputName]: fourthReactHookFormProps.onChange,
    }

    void options[target.name](event)
  }

  return (
    <>
      <Styled.Wrapper
        $error={error && showValidation}
        $hasSomethingTyped={hasSomethingTyped}
      >
        <Styled.Code
          {...firstReactHookFormProps}
          maxLength={1}
          onChange={handleChange}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          data-testid="codefield-first-input"
        />
        <Styled.Code
          {...secondReactHookFormProps}
          maxLength={1}
          onChange={handleChange}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          data-testid="codefield-second-input"
        />
        <Styled.Code
          {...thirdReactHookFormProps}
          maxLength={1}
          onChange={handleChange}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          data-testid="codefield-third-input"
        />
        <Styled.Code
          {...fourthReactHookFormProps}
          maxLength={1}
          onChange={handleChange}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          data-testid="codefield-fourth-input"
        />
      </Styled.Wrapper>
      {error && showValidation && <Error as="p">{error}</Error>}
    </>
  )
}

export default CodeField
